import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import RequestAccessModal from "./RequestAccessModal";
import { useLang } from "@/contexts/LanguageContext";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const location = useLocation();
  const { lang, setLang, t } = useLang();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location]);

  const navLinks = [
    { label: t("nav_events"), href: "/#events" },
    { label: t("nav_about"), href: "/#why-serava" },
    { label: t("nav_how"), href: "/#how-it-works" },
    { label: t("nav_contact"), href: "/#contact" },
  ];

  return (
    <>
      <nav data-testid="navbar" className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? "nav-glass" : "bg-transparent"}`}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-20">
            <Link to="/" data-testid="nav-logo" className="flex flex-col items-start group">
              <span className="text-[#C5A059] font-semibold text-base tracking-[0.3em] uppercase font-['Outfit']">SERAVA</span>
              <span className="text-white/40 text-[9px] tracking-[0.25em] uppercase font-['Outfit'] font-light -mt-0.5">Sportsevents</span>
            </Link>

            <div className="hidden md:flex items-center gap-10">
              {navLinks.map((link) => (
                <a key={link.label} href={link.href} className="text-xs tracking-[0.15em] uppercase text-white/60 hover:text-[#C5A059] transition-colors duration-300 font-['Outfit'] font-medium">{link.label}</a>
              ))}

              {/* Language switcher */}
              <div className="flex items-center border border-[#262626] text-[10px] font-['Outfit'] tracking-wider" data-testid="lang-switcher">
                <button onClick={() => setLang("sv")} className={`px-2.5 py-1.5 transition-colors ${lang === "sv" ? "bg-[#C5A059]/10 text-[#C5A059]" : "text-white/40 hover:text-white"}`}>SVE</button>
                <button onClick={() => setLang("en")} className={`px-2.5 py-1.5 transition-colors ${lang === "en" ? "bg-[#C5A059]/10 text-[#C5A059]" : "text-white/40 hover:text-white"}`}>EN</button>
              </div>

              <button onClick={() => setShowRequestModal(true)} data-testid="nav-request-access-btn" className="btn-primary px-6 py-2.5 text-xs tracking-[0.15em] uppercase font-['Outfit']">{t("nav_request")}</button>
            </div>

            <button data-testid="mobile-menu-toggle" className="md:hidden text-white/80 hover:text-[#C5A059] transition-colors" onClick={() => setMobileOpen(!mobileOpen)}>
              {mobileOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="fixed inset-0 z-40 bg-[#0A0A0A]/98 backdrop-blur-xl pt-24 px-8">
            <div className="flex flex-col gap-8">
              {navLinks.map((link, i) => (
                <motion.a key={link.label} href={link.href} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.1 }} onClick={() => setMobileOpen(false)}
                  className="text-2xl font-['Cormorant_Garamond'] font-light text-white/80 hover:text-[#C5A059] transition-colors">{link.label}</motion.a>
              ))}
              {/* Mobile language switcher */}
              <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 }} className="flex items-center gap-3">
                <button onClick={() => { setLang("sv"); setMobileOpen(false); }} className={`px-4 py-2 border text-sm font-['Outfit'] ${lang === "sv" ? "border-[#C5A059] text-[#C5A059]" : "border-[#262626] text-white/40"}`}>Svenska</button>
                <button onClick={() => { setLang("en"); setMobileOpen(false); }} className={`px-4 py-2 border text-sm font-['Outfit'] ${lang === "en" ? "border-[#C5A059] text-[#C5A059]" : "border-[#262626] text-white/40"}`}>English</button>
              </motion.div>
              <motion.button initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5 }} onClick={() => { setMobileOpen(false); setShowRequestModal(true); }}
                data-testid="mobile-request-access-btn" className="btn-primary inline-block text-center px-8 py-4 text-sm tracking-[0.15em] uppercase mt-4 font-['Outfit']">{t("nav_request")}</motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {showRequestModal && <RequestAccessModal onClose={() => setShowRequestModal(false)} />}
    </>
  );
}
